import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RadarItem } from '@/services/predictiveCalendarService';
import { predictiveCalendarService } from '@/services/predictiveCalendarService';
import { destinationsApi } from '@/services/config';

interface DraftBuilderDrawerProps {
  item: RadarItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

export const DraftBuilderDrawer: React.FC<DraftBuilderDrawerProps> = ({
  item,
  isOpen,
  onClose,
  onSuccess,
}) => {
  // Hooks ANTES do guard de render — return condicional acima de hook quebra
  // a contagem entre renders (mesma família do F-236 no ArticleEditor).
  const defaultTitle = `${item?.termo ?? ''}: O Guia Completo e Atualizado`;
  const [title, setTitle] = useState(defaultTitle);
  const [insertAffiliates, setInsertAffiliates] = useState(true);
  const [selectedSite, setSelectedSite] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Destinos REAIS do acesso — não uma lista fixa de portais de um cliente.
  const { data: sites } = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => destinationsApi.list(),
    staleTime: 5 * 60_000,
  });
  // O drawer fica montado o tempo todo; quando a pauta muda, o título proposto
  // acompanha (o useState só lê o valor inicial na montagem).
  useEffect(() => {
    if (item) setTitle(`${item.termo}: O Guia Completo e Atualizado`);
  }, [item]);

  if (!isOpen || !item) return null;

  const handleCreate = async () => {
    if (!selectedSite) {
      setError('Escolha o site de destino antes de criar o rascunho.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await predictiveCalendarService.generateDraft({
        termo: item.termo,
        categoria: item.categoria,
        // Zero quando a ocasião não tem antecedência medida: o backend só usa
        // este campo como rótulo do rascunho, e nulo quebraria o contrato.
        lead_time_dias: item.lead_time_dias ?? 0,
        insert_affiliates: insertAffiliates,
        site_destino: selectedSite,
        custom_title: title,
      });
      onSuccess(res.message);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error && err.message ? err.message : 'Falha ao criar o rascunho preditivo.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '460px',
          maxWidth: '90vw',
          height: '100%',
          backgroundColor: 'var(--bg-0, #0F172A)',
          borderLeft: '1px solid var(--border-2)',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.4)',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            <div>
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--sig-warn)',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Pauta Builder
              </span>
              <h2
                style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: 'var(--text-1)',
                  margin: '4px 0 0 0',
                }}
              >
                Gerar Rascunho Preditivo
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-3)',
                fontSize: '20px',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>

          {error && (
            <div
              style={{
                backgroundColor: 'rgba(239,68,68,0.15)',
                border: '1px solid var(--sig-crit)',
                color: 'var(--sig-crit)',
                padding: '10px',
                borderRadius: '6px',
                fontSize: '12px',
                marginBottom: '16px',
              }}
            >
              {error}
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-2)',
                marginBottom: '6px',
              }}
            >
              Título H1 Otimizado (SEO & Google Discover)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: 'var(--bg-2)',
                border: '1px solid var(--border-2)',
                borderRadius: '6px',
                color: 'var(--text-1)',
                fontSize: '13px',
                outline: 'none',
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-2)',
                marginBottom: '6px',
              }}
            >
              Site / Portal de Destino
            </label>
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: 'var(--bg-2)',
                border: '1px solid var(--border-2)',
                borderRadius: '6px',
                color: 'var(--text-1)',
                fontSize: '13px',
                outline: 'none',
              }}
            >
              <option value="">— escolha o destino —</option>
              {(sites ?? []).map((s: { slug: string; name: string }) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* A promessa antiga era "links com tags ATIVAS ... para comissoes
              automaticas". O unico lugar do produto que monta caixa de afiliado
              e predictive_calendar.py:163-167, e ele grava DUAS URLs fixas e SEM
              TAG (booking.com e amazon.com.br), sem ler affiliate_amazon_tag /
              affiliate_shopee_id / affiliate_booking_url / affiliate_decolar_url.
              Shopee e Decolar nao aparecem em ramo nenhum. O rotulo esta colado
              no proprio texto do checkbox, que e o controle que promete. */}
          <div
            style={{
              backgroundColor: 'var(--bg-2)',
              border: '1px solid var(--border-1)',
              borderRadius: '8px',
              padding: '14px',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '8px',
              }}
            >
              <span
                style={{
                  fontSize: '12.5px',
                  fontWeight: 600,
                  color: 'var(--text-1)',
                  lineHeight: 1.4,
                }}
              >
                💰 Injetar Caixas de Afiliados &amp; Patrocínio{' '}
                <span style={{ fontWeight: 500, color: 'var(--text-3)' }}>
                  (tags de afiliado ainda não configuradas — sai sem links de comissão; em
                  construção)
                </span>
              </span>
              <input
                type="checkbox"
                checked={insertAffiliates}
                onChange={(e) => setInsertAffiliates(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer',
                  marginTop: '2px',
                  flexShrink: 0,
                }}
              />
            </div>
            <p style={{ fontSize: '11px', color: 'var(--text-3)', margin: 0, lineHeight: 1.4 }}>
              Nenhuma tag é aplicada: em pautas de turismo ou varejo a prévia sai com um link
              genérico para Booking ou Amazon, sem identificador de afiliado e sem comissão; nas
              demais categorias não sai caixa nenhuma. Shopee e Decolar ainda não têm caixa.
            </p>
          </div>

          {/* Aqui se lia "Esta materia sera cadastrada no Kanban de Producao com
              o selo de Pauta Preditiva". O endpoint nao persiste nada:
              predictive_calendar.py:181-193 responde status "preview",
              persistido: false, story_id: null — nao ha db.add nem commit no
              arquivo inteiro. O selo "Pauta Preditiva" nao existe do lado do
              Quadro. O texto passa a dizer o que o clique faz de fato. */}
          <div
            style={{
              backgroundColor: 'rgba(37,99,235,0.08)',
              border: '1px solid rgba(37,99,235,0.2)',
              borderRadius: '8px',
              padding: '12px',
              fontSize: '11.5px',
              color: 'var(--nexus-brand-hover)',
              lineHeight: 1.4,
            }}
          >
            ℹ️ <strong>Estratégia de Lead Time:</strong> o valor desta pauta é a antecedência — a
            janela sugerida é de {item.lead_time_dias} dias antes do pico de busca.{' '}
            <strong>Ainda não conectado:</strong> este botão monta a <i>prévia</i> do texto e não
            cria card no Quadro de Produção; a mensagem que volta diz onde o texto ficou (em
            construção).
          </div>
        </div>

        <div
          style={{
            borderTop: '1px solid var(--border-1)',
            paddingTop: '16px',
            display: 'flex',
            gap: '10px',
          }}
        >
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              flex: 1,
              padding: '10px',
              backgroundColor: 'var(--border-1)',
              color: 'var(--text-1)',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '12.5px',
              cursor: 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleCreate}
            disabled={loading}
            style={{
              flex: 2,
              padding: '10px',
              backgroundColor: 'var(--nexus-brand-active)',
              color: 'var(--text-1)',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 700,
              fontSize: '12.5px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            {/* O botao dizia "Criar no Kanban" e nao cria: o endpoint devolve
                preview / persistido:false. O clique e o mesmo — muda o rotulo,
                que passa a prometer o que a resposta entrega. */}
            {loading ? 'Gerando prévia...' : '🚀 Gerar prévia do rascunho'}
          </button>
        </div>
      </div>
    </div>
  );
};
